import firebase from 'firebase/app';
import 'firebase/auth';
import 'firebase/firestore';
import { auth, db, googleProvider, githubProvider } from '../config/firebase';

interface UserProfile {
  uid: string;
  displayName: string;
  email: string;
  photoURL: string;
  lastLogin: string;
}

interface UserCredential {
  user: firebase.User | null;
  credential: firebase.auth.AuthCredential | null;
  additionalUserInfo?: firebase.auth.AdditionalUserInfo | null;
}

export const signInWithGoogle = async (): Promise<UserCredential> => {
  try {
    const result = await auth.signInWithPopup(googleProvider);
    if (result.user) {
      await updateUserProfile(result.user.uid, {
        displayName: result.user.displayName || '',
        email: result.user.email || '',
        photoURL: result.user.photoURL || '',
        lastLogin: new Date().toISOString()
      });
    }
    return result;
  } catch (error) {
    console.error('Error signing in with Google:', error);
    throw error;
  }
};

export async function signInWithGithub(): Promise<void> {
  await auth.signInWithPopup(githubProvider);
}

export async function signOut(): Promise<void> {
  await auth.signOut();
}

export function onAuthStateChanged(callback: (user: firebase.User | null) => void): () => void {
  return auth.onAuthStateChanged(callback);
}

export function getCurrentUser(): firebase.User | null {
  return auth.currentUser;
}

export async function updateUserProfile(uid: string, profile: Partial<UserProfile>): Promise<void> {
  await db.collection('users').doc(uid).set(profile, { merge: true });
}

export const signInWithEmail = async (email: string, password: string): Promise<void> => {
  try {
    const result = await auth.signInWithEmailAndPassword(email, password);
    console.log('Successfully signed in with email:', result.user?.email);
  } catch (error: any) {
    console.error('Email sign-in error:', error);
    if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
      throw new Error('Invalid email or password');
    } else if (error.code === 'auth/invalid-email') {
      throw new Error('Invalid email address');
    } else if (error.code === 'auth/user-disabled') {
      throw new Error('This account has been disabled');
    }
    throw error;
  }
};

export const signUpWithEmail = async (email: string, password: string): Promise<void> => {
  try {
    const result = await auth.createUserWithEmailAndPassword(email, password);
    console.log('Successfully created account:', result.user?.email);
  } catch (error: any) {
    console.error('Email sign-up error:', error);
    if (error.code === 'auth/email-already-in-use') {
      throw new Error('This email is already registered');
    } else if (error.code === 'auth/invalid-email') {
      throw new Error('Invalid email address');
    } else if (error.code === 'auth/operation-not-allowed') {
      throw new Error('Email/password accounts are not enabled. Please contact support.');
    } else if (error.code === 'auth/weak-password') {
      throw new Error('Password is too weak');
    }
    throw error;
  }
};

export type User = firebase.User;